
import { Options, Vue } from "vue-class-component";
import AdvanceFilter from "./components/advance-filter.vue";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import checkRolePermission from "@/app/infrastructures/misc/common-library/CheckRolePermission";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Constants";
import { CLAIM } from "@/app/infrastructures/misc/RolePermission";
import { ClaimController } from "@/app/ui/controllers/ClaimController";
import { ClaimData, ClaimDetailData } from "@/domain/entities/Claim";
import PagePopupDetail from "@/app/ui/views/claim/page-popup-detail/index.vue";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import BottomSheet from "@/app/ui/components/bottom-sheet/index.vue";
import LpRadio from "@/app/ui/components/lp-radio/index.vue";

@Options({
  components: {
    LpRadio,
    BottomSheet,
    AdvanceFilter,
    PagePopupDetail
  }
})
class Claim extends Vue {
  isSheetOpen = false;

  openFilterPopUp() {
    this.isSheetOpen = true
    this.claimStatus = ClaimController.status
    this.claimCategory = ClaimController.category
  }

  // Claim Status
  claimStatus = "";

  get isDetailAble() {
    return IS_SENDER_ACCOUNT ? true : checkRolePermission(CLAIM.DETAIL);
  }

  get claimStatusOptions() {
    return ["submitted", "approved", "rejected", "finished"]
  }

  get claimStatusLabels() {
    return [this.$t("claim.submitted"), this.$t("claim.approved"), this.$t("claim.rejected"), this.$t("claim.finished")]
  }

  onSelectStatus() {
    ClaimController.setStatus(this.claimStatus);
  }

  // Claim Category
  claimCategory = "";

  get claimCategoryOptions() {
    return ["lost", "damaged", "mbg"]
  }

  get claimCategoryLabels() {
    return [this.$t("claim.missing"), this.$t("claim.damaged"), this.$t("claim.overdue")]
  }

  onSelectCategory() {
    ClaimController.setCategory(this.claimCategory);
  }

  onSelectClaimFilter() {
    this.onSelectCategory()
    this.onSelectStatus()
    this.isSheetOpen = false
    ClaimController.onSelectFilterClaim()
  }

  get isAnyFilterSelected() {
    return this.claimCategory != "" || this.claimStatus != ""
  }

  resetClaimFilter() {
    this.claimCategory = ""
    this.claimStatus = ""
  }

  get searchValue() {
    return ClaimController.searchValue;
  }
  onSearch(value: string) {
    if (value.length > 3 || value.length === 0) {
      ClaimController.onSearchValue(value);
    }
  }

  beforeUnmount() {
    ClaimController.setStatus("");
    ClaimController.setCategory("");
    ClaimController.setSearch("");
  }

  clearSearch() {
    ClaimController.onClearSearch();
  }

  created() {
    let defaultQueryObject: any = "";
    if (this.$route.query?.q) {
      const urlParams = new URLSearchParams(atob(String(this.$route.query.q)));
      defaultQueryObject = Object.fromEntries(urlParams.entries());
    }
    ClaimController.initDataClaimList(defaultQueryObject);
  }

  initClaim() {
    ClaimController.initDataClaimList();
  }

  get firstRequest() {
    return ClaimController.firstRequest;
  }

  get isLoading() {
    return ClaimController.isLoading;
  }

  get isError() {
    return ClaimController.isError;
  }

  get errorCause() {
    return ClaimController.errorCause;
  }

  get dataClaims() {
    return ClaimController.claimData.data;
  }

  get columns() {
    return [
      {
        name: this.$t('claim.columns.colSttNoResi'),
        key: "noResi",
        styleHead: "w-56 text-left whitespace-no-wrap",
        render: (item: ClaimData) => {
          return `<span class='text-left text-red-lp-200 flex justify-center'>${item.noResi}</span>`;
        }
      },
      {
        name: this.$t("claim.columns.colShipmentId"),
        key: "shipmentId",
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: ClaimData) => {
          return `<span class='text-left text-black-lp-300 flex justify-center'>${item.shipmentId}</span>`;
        }
      },
      {
        name: this.$t("claim.columns.colProduct"),
        key: "produk",
        styleHead: "w-32 text-center",
        render: (item: ClaimData) => {
          return `
<div class="text-black-lp-200 flex text-center">
<div class="rounded px-2 py-0 bg-gray-lp-400">
<span class="capitalize">${item.productType}</span>
</div>
</div>`;
        }
      },
      {
        name: this.$t("claim.columns.colCategory"),
        key: "category",
        styleHead: "w-32 text-left whitespace-no-wrap",
        render: (item: ClaimData) => {
          const key = (item.category || "").toLowerCase();
          const label =
            key === "lost"
              ? this.$t("claim.missing")
              : key === "damaged"
              ? this.$t("claim.damaged")
              : key === "mbg"
              ? this.$t("claim.overdue")
              : item.categoryName;
          return `<span class='text-left text-black-lp-300 flex'>${label}</span>`;
        }
      },
      {
        name: this.$t("claim.columns.colClaimStatus"),
        key: "status",
        styleHead: "w-56 text-left text-black-lp-300 whitespace-no-wrap",
        render: (item: ClaimData) => {
          const key = (item.status || "").toLowerCase();
          const label =
            key === "submitted"
              ? this.$t("claim.submitted")
              : key === "approved"
              ? this.$t("claim.approved")
              : key === "rejected"
              ? this.$t("claim.rejected")
              : key === "finished"
              ? this.$t("claim.finished")
              : item.statusName;
          return `<div class='text-left text-black-lp-300 whitespace-no-wrap text-12px xxl:text-14px rounded-full capitalize px-2 py-0 bg-gray-lp-400 font-medium'>
${label}
</div>`;
        },
        toolTip: (item: ClaimData) => {
          const key = (item.status || "").toLowerCase();
          const label =
            key === "submitted"
              ? this.$t("claim.submitted")
              : key === "approved"
              ? this.$t("claim.approved")
              : key === "rejected"
              ? this.$t("claim.rejected")
              : key === "finished"
              ? this.$t("claim.finished")
              : item.statusName;
          return `<div class='flex text-black-lp-300'>Klaim ${label}</div>`;
        }
      },
      {
        name: this.$t("claim.columns.colUpdatedStatus"),
        key: "statusDate",
        styleHead: "w-56 text-left whitespace-no-wrap",
        render: (item: ClaimData) => {
          return `<span class='text-black-lp-300 flex justify-center'>${formatDate(
item.statusDate
)}</span>`;
        }
      }
    ];
  }

  onRequest() {
    ClaimController.loadDataClaimList();
  }

  dataDetailClaim: ClaimDetailData = new ClaimDetailData();
  dialogDetailClaim = false;
  async onClickRow(item: ClaimData) {
    MainAppController.showLoading();
    try {
      this.dataDetailClaim = await ClaimController.GetDetailClaim({
        sttNo: item.noResi
      });
      this.dialogDetailClaim = true;
    } catch (err) {
      MainAppController.showErrorMessage(parsingErrorResponse(err));
      this.dataDetailClaim = new ClaimDetailData();
    } finally {
      MainAppController.closeLoading();
    }
  }

  get pagination() {
    return ClaimController.claimData.pagination;
  }
}
export default Claim;
